import {createRouter, createWebHistory, RouterOptions} from 'vue-router';
import List from '@/views/List.vue';
import Login from '@/views/Login.vue';
import CustomerFilePage from "@/views/CustomerFilePage.vue";
import QRCode from "@/views/QRCode.vue";
import Auth from "@/views/Auth.vue";
import {ROLES} from "@/api/models";
import Error from "@/views/Error.vue";
import Logout from "@/views/Logout.vue";
import Flush from "@/views/Flush.vue";
import LogsView from "@/views/Logs.vue";
import NotificationsHistory from "@/views/NotificationsHistory.vue";
import NotificationsHistoryDetails from "@/views/NotificationsHistoryDetails.vue";
import Admins from "@/views/Admins.vue";
import Settings from "@/views/Settings.vue";
import {Logs} from "@/utils/Logs";
import {manageLocalhostRedirection} from "@/services/devService";
import {useSearchStore} from "@/store";
import {useAuthStore} from "@/store/pinia/authStore";

// Async loading needed to prevent overwrites from fo css
const ProductFilePage = () => import("@/views/ProductFilePage.vue");

const log = new Logs('Router', true);

const routes: RouterOptions['routes'] = [
  {
    path: '/',
    name: 'list',
    component: List,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
    beforeEnter: (to, from, next) => {
      if (to.redirectedFrom?.name !== 'filtered-list') {
        const searchStore = useSearchStore();
        searchStore.resetFilters();
      }
      return next();
    },
  },
  {
    path: '/filtered-list',
    name: 'filtered-list',
    redirect: {name: 'list'},
  },
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: {
      requireLogged: false,
      authorizedRoles: [],
      layout: {
        default: 'basic',
      },
    },
  },
  {
    path: '/auth',
    name: 'auth',
    component: Auth,
    meta: {
      requireLogged: false,
      authorizedRoles: [],
      layout: {
        default: 'basic',
      },
    },
  },
  {
    path: '/customer-file-page/:id',
    name: 'customer-file-page',
    component: CustomerFilePage,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
      relatedRouteNames: ['list'],
    },
  },
  {
    path: '/product-file-page/:customerId/:serialId',
    name: 'product-file-page',
    component: ProductFilePage,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
      relatedRouteNames: ['list'],
    },
  },
  {
    path: '/qr-code',
    name: 'qr-code',
    component: QRCode,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.FA, ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        asAdmin: 'full',
        default: 'basic',
      },
    },
  },
  {
    path: '/error',
    name: 'error',
    component: Error,
    meta: {
      requireLogged: false,
      authorizedRoles: [ROLES.FA, ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        asAdmin: 'full',
        default: 'simple',
      },
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: Logout,
    meta: {
      requireLogged: false,
      authorizedRoles: [ROLES.FA, ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'basic',
      },
    },
  },
  {
    path: '/logs',
    name: 'logs',
    component: LogsView,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
  },
  {
    path: '/notifications-history',
    name: 'notifications-history',
    component: NotificationsHistory,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
  },
  {
    path: '/notifications-history/:id',
    name: 'notifications-history-details',
    component: NotificationsHistoryDetails,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.LOCAL_ADMIN, ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
      relatedRouteNames: ['notifications-history'],
    },
  },
  {
    path: '/flush',
    name: 'flush',
    component: Flush,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
  },
  {
    path: '/admins',
    name: 'admins',
    component: Admins,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
  },
  {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: {
      requireLogged: true,
      authorizedRoles: [ROLES.SUPER_ADMIN],
      layout: {
        default: 'full',
      },
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => `/`,
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return {
      top: 0,
    };
  },
});

router.beforeEach((to, from, next) => {

  const authStore = useAuthStore();

  const authorizedRoles = to.meta.authorizedRoles as ROLES[];
  const requireLogged = to.meta.requireLogged as boolean;

  if (to.name === 'auth') {
    manageLocalhostRedirection(to.fullPath);
  }

  // Log in the user if needed
  if (requireLogged && !authStore.isConnected) {
    return next({name: 'login'});
  }

  const isAuthorized = (authorizedRoles: ROLES[]) => {
    return authorizedRoles.some((r) => {
      return authStore.roles.includes(r);
    });
  }

  const requireNotLogged = !requireLogged && authorizedRoles.length === 0;
  if (requireNotLogged && authStore.isConnected) {
    next({name: authStore.defaultPageName});
  }

  if (requireNotLogged || isAuthorized(authorizedRoles)) {
    return next();
  } else {
    next({name: authStore.defaultPageName});
  }
});

export default router;
